import { memo } from 'react';
import { Area, AreaChart, CartesianGrid, Legend, Tooltip, XAxis, YAxis } from 'recharts';

import { ChartFrame } from '@/components/charts/ChartFrame';
import type { Tier } from '@/domain/types';
import type { MonthlySnapshot } from '@/domain/engine';
import { formatNumber } from '@/lib/format';
import { getTierColor } from '@/lib/tierColors';

type ClientsByTierChartProps = {
  snapshots: MonthlySnapshot[];
  tiers: Tier[];
};

type ChartDatum = { month: number } & Record<string, number>;

const TOOLTIP_STYLE = {
  backgroundColor: 'var(--card)',
  border: '1px solid var(--border)',
  color: 'var(--card-foreground)',
  fontSize: 12,
};

export const ClientsByTierChart = memo(function ClientsByTierChart({
  snapshots,
  tiers,
}: ClientsByTierChartProps) {
  const data: ChartDatum[] = snapshots.map((s) => {
    const datum: ChartDatum = { month: s.month };
    for (const tier of tiers) {
      datum[tier.id] = Math.round(s.clientsPerTier[tier.id] ?? 0);
    }
    return datum;
  });

  return (
    <ChartFrame>
      <AreaChart data={data} margin={{ top: 4, right: 16, left: 8, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
        <XAxis
          dataKey="month"
          tickFormatter={(v: number) => `M${v}`}
          tick={{ fill: 'currentColor', fontSize: 11 }}
        />
        <YAxis
          tickFormatter={(v: number) => formatNumber(v)}
          tick={{ fill: 'currentColor', fontSize: 11 }}
          width={40}
        />
        <Tooltip
          formatter={(value, name) =>
            typeof value === 'number' ? [formatNumber(value), String(name)] : value
          }
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={(label) => `Mois ${String(label)}`}
        />
        <Legend wrapperStyle={{ fontSize: 12 }} />
        {tiers.map((tier, i) => {
          const color = getTierColor(i);
          return (
            <Area
              key={tier.id}
              type="monotone"
              dataKey={tier.id}
              name={tier.name}
              stackId="clients"
              stroke={color}
              fill={color}
              fillOpacity={0.6}
              isAnimationActive={false}
            />
          );
        })}
      </AreaChart>
    </ChartFrame>
  );
});
